/**
 * ChatWindow — active conversation view.
 *
 * Shows: message history (paginated), typing indicators,
 * ChatInput at bottom. Handles message CRUD via API + SignalR.
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchMessages, sendMessage, editMessage, deleteMessage,
  togglePin, fetchPinnedMessages, toggleReaction, reportMessage,
  searchMessages,
} from '../../api/chat';
import type { MessageResponse, ConversationResponse } from '../../api/chat';
import { onChatEvent, sendMarkRead, ChatHubEvents, joinConversationGroup } from '../../api/chatConnection';
import { setActiveConversation, markConversationNotificationsAsRead } from '../../notifications/notificationStore';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import GroupMembersPanel from './GroupMembersPanel';
import styles from './Chat.module.css';

interface ChatWindowProps {
  conversation: ConversationResponse;
  currentUserGuid: string;
  /** DEV-1391 — called after a conversation change (e.g. group avatar) so the
   *  parent re-fetches the active conversation + list. */
  onConversationUpdated?: () => void;
}

export default function ChatWindow({ conversation, currentUserGuid, onConversationUpdated }: ChatWindowProps) {
  const { t } = useAppTranslation('chat');
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());
  const [sendError, setSendError] = useState<string | null>(null);
  const [showMembers, setShowMembers] = useState(false);
  const [showPinnedMessages, setShowPinnedMessages] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState<MessageResponse[]>([]);
  const [loadingPinned, setLoadingPinned] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MessageResponse[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingOlderRef = useRef(false);
  // Tracks whether the user is scrolled near the bottom of the messages list.
  // Used by the SignalR NewMessage handler to decide whether to auto-scroll on
  // arrival. A ref (not state) so the SignalR callback — registered in a useEffect
  // keyed on conversation.id — reads the current value instead of a stale snapshot.
  const isNearBottomRef = useRef(true);

  // Load initial messages + ensure we're in the SignalR group
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setEditingMessageId(null);

    // Tell notification store we're viewing this conversation (suppresses notifications)
    setActiveConversation(conversation.id);
    // Mark any existing notifications for this conversation as read
    markConversationNotificationsAsRead(conversation.id);

    // Ensure we're in the SignalR group for this conversation
    // (handles newly created conversations where OnConnectedAsync didn't join us)
    joinConversationGroup(conversation.id).catch(() => {});

    fetchMessages(conversation.id)
      .then((msgs) => {
        if (!cancelled) {
          // API returns newest first, we display oldest first
          setMessages(msgs.reverse());
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('[ChatWindow] Failed to load messages:', err);
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      // Clear active conversation when leaving
      setActiveConversation(null);
    };
  }, [conversation.id]);

  // Initial-load scroll: jump to the bottom synchronously before paint so the
  // newest message is visible from frame zero — no oldest-at-top flash, no
  // smooth-scroll animation. Fires on initial fetch completion (loading flips
  // false) and when switching conversations. New-message auto-scroll is handled
  // imperatively inside the SignalR NewMessage handler and handleSend so we can
  // guard the SignalR case on isNearBottomRef (prevents yanking users who have
  // scrolled up to read history). DEV-1035.
  useLayoutEffect(() => {
    if (!loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      isNearBottomRef.current = true;
    }
  }, [loading, conversation.id]);

  // Mark latest message as read when conversation opens or new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.senderGuid.toUpperCase() !== currentUserGuid.toUpperCase()) {
        sendMarkRead(conversation.id, lastMsg.id).then(() => {
          // Trigger messages badge refresh after server processes the read receipt.
          // Fire immediately for responsiveness, then again after a short delay
          // to catch cases where the server hasn't updated unreadCount yet.
          try { window.dispatchEvent(new CustomEvent('l4e_badges_changed')); } catch { /* */ }
          setTimeout(() => {
            try { window.dispatchEvent(new CustomEvent('l4e_badges_changed')); } catch { /* */ }
          }, 500);
        }).catch(() => {});
      }
    }
  }, [messages.length, conversation.id, currentUserGuid]);

  // Subscribe to real-time events
  useEffect(() => {
    const unsubs: Array<() => void> = [];

    // New message (dedupe: skip if already in list from optimistic add)
    // Normalize attachments array to ensure it's always present and well-formed
    // (SignalR may deliver it slightly differently than REST responses)
    unsubs.push(onChatEvent(ChatHubEvents.NewMessage, (raw: any) => {
      const msg: MessageResponse = {
        ...raw,
        attachments: Array.isArray(raw.attachments)
          ? raw.attachments.map((a: any) => ({
              id: a.id,
              fileKey: a.fileKey,
              fileName: a.fileName,
              fileSizeBytes: a.fileSizeBytes ?? 0,
              contentType: a.contentType ?? 'application/octet-stream',
              thumbnailKey: a.thumbnailKey,
            }))
          : [],
        reactions: Array.isArray(raw.reactions) ? raw.reactions : [],
      };
      if (msg.conversationId === conversation.id) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        // Only auto-scroll if the user is near the bottom. If they've scrolled
        // up to read history, leave their position alone — incoming messages
        // should not yank them out of context. DEV-1035.
        if (isNearBottomRef.current) {
          requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          });
        }
      }
    }));

    // Message edited
    unsubs.push(onChatEvent(ChatHubEvents.MessageEdited, (msg: MessageResponse) => {
      if (msg.conversationId === conversation.id) {
        setMessages((prev) => prev.map((m) => m.id === msg.id ? msg : m));
      }
    }));

    // Message deleted
    unsubs.push(onChatEvent(ChatHubEvents.MessageDeleted, (data: { conversationId: string; messageId: string }) => {
      if (data.conversationId === conversation.id) {
        setMessages((prev) => prev.map((m) =>
          m.id === data.messageId ? { ...m, isDeleted: true, content: null } : m
        ));
      }
    }));

    // Reaction updated
    unsubs.push(onChatEvent(ChatHubEvents.ReactionUpdated, (data: { conversationId: string; messageId: string; reactions: any[] }) => {
      if (data.conversationId === conversation.id) {
        setMessages((prev) => prev.map((m) =>
          m.id === data.messageId ? { ...m, reactions: data.reactions } : m
        ));
      }
    }));

    // Typing indicator
    unsubs.push(onChatEvent(ChatHubEvents.TypingIndicator, (data: { conversationId: string; userGuid: string; isTyping: boolean }) => {
      if (data.conversationId === conversation.id) {
        setTypingUsers((prev) => {
          const next = new Map(prev);
          if (data.isTyping) {
            // Find display name from participants
            const p = conversation.participants.find(
              (pp) => pp.userGuid.toUpperCase() === data.userGuid.toUpperCase()
            );
            next.set(data.userGuid, p?.displayName || '...');
          } else {
            next.delete(data.userGuid);
          }
          return next;
        });
      }
    }));

    // Pin
    unsubs.push(onChatEvent(ChatHubEvents.MessagePinned, (data: { conversationId: string; messageId: string; isPinned: boolean }) => {
      if (data.conversationId === conversation.id) {
        setMessages((prev) => prev.map((m) =>
          m.id === data.messageId ? { ...m, isPinned: data.isPinned } : m
        ));
      }
    }));

    return () => { unsubs.forEach((fn) => fn()); };
  }, [conversation.id, conversation.participants]);

  // Track near-bottom for the SignalR NewMessage auto-scroll guard, and
  // lazy-load older messages when the user scrolls near the top.
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    // ~120px threshold from absolute bottom. Anything inside that is treated
    // as "still reading the live tail" — new messages will auto-scroll into
    // view. Outside it, the user is reading history and we leave them alone.
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 120;

    if (loadingOlderRef.current || messages.length === 0) return;
    if (el.scrollTop < 50) {
      loadingOlderRef.current = true;
      const oldestId = messages[0]?.id;
      fetchMessages(conversation.id, oldestId, 50)
        .then((older) => {
          if (older.length > 0) {
            setMessages((prev) => [...older.reverse(), ...prev]);
          }
          loadingOlderRef.current = false;
        })
        .catch(() => { loadingOlderRef.current = false; });
    }
  }, [conversation.id, messages]);

  // Message actions
  const handleSend = useCallback(async (content: string, attachmentFileKeys: string[] = []) => {
    if (editingMessageId) {
      try {
        await editMessage(conversation.id, editingMessageId, content);
        setEditingMessageId(null);
      } catch (err: any) {
        alert(err.message || t('conversation.editWindowExpired'));
      }
    } else {
      try {
        setSendError(null);
        const msg = await sendMessage(conversation.id, content, 'text', attachmentFileKeys);
        // Optimistically add the message if it's not already in the list
        // (SignalR broadcast may arrive first)
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        // Always scroll to the bottom on own-send — the user just hit send
        // and expects to see their message land. The smooth animation here IS
        // the feedback that the send succeeded. DEV-1035.
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          isNearBottomRef.current = true;
        });
      } catch (err: any) {
        console.error('[ChatWindow] Send failed:', err);
        setSendError(err.message || 'Failed to send message');
        // Auto-clear error after 5 seconds
        setTimeout(() => setSendError(null), 5000);
      }
    }
  }, [conversation.id, editingMessageId, t]);

  const handleEdit = useCallback((messageId: string) => {
    setEditingMessageId(messageId);
  }, []);

  const handleDelete = useCallback(async (messageId: string) => {
    try {
      await deleteMessage(conversation.id, messageId);
    } catch (err) {
      console.error('[ChatWindow] Delete failed:', err);
    }
  }, [conversation.id]);

  const handlePin = useCallback(async (messageId: string) => {
    try {
      await togglePin(conversation.id, messageId);
    } catch (err) {
      console.error('[ChatWindow] Pin failed:', err);
    }
  }, [conversation.id]);

  const handleReact = useCallback(async (messageId: string, emoji: string) => {
    try {
      await toggleReaction(conversation.id, messageId, emoji);
    } catch (err) {
      console.error('[ChatWindow] Reaction failed:', err);
    }
  }, [conversation.id]);

  const handleReport = useCallback(async (messageId: string) => {
    const reason = prompt(t('report.title'));
    if (reason) {
      try {
        await reportMessage(conversation.id, messageId, reason);
      } catch (err) {
        console.error('[ChatWindow] Report failed:', err);
      }
    }
  }, [conversation.id, t]);

  // Find editing content
  const editingContent = editingMessageId
    ? messages.find((m) => m.id === editingMessageId)?.content || null
    : null;

  // Typing indicator text
  const typingText = useMemo(() => {
    const names = Array.from(typingUsers.values());
    if (names.length === 0) return null;
    if (names.length === 1) return t('conversation.typingOne', { name: names[0] });
    return t('conversation.typingMultiple', { count: names.length });
  }, [typingUsers, t]);

  // Determine user role for permission checks
  const myParticipant = conversation.participants.find(
    (p) => p.userGuid.toUpperCase() === currentUserGuid.toUpperCase()
  );
  const isAdmin = myParticipant?.role === 'admin';

  // ── Pinned messages ──
  const handleShowPinned = useCallback(async () => {
    if (showPinnedMessages) {
      setShowPinnedMessages(false);
      return;
    }
    setLoadingPinned(true);
    setShowPinnedMessages(true);
    try {
      const pinned = await fetchPinnedMessages(conversation.id);
      setPinnedMessages(pinned);
    } catch (err) {
      console.error('[ChatWindow] Failed to load pinned messages:', err);
    }
    setLoadingPinned(false);
  }, [conversation.id, showPinnedMessages]);

  // ── Search ──
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const results = await searchMessages(conversation.id, query);
      setSearchResults(results);
    } catch (err) {
      console.error('[ChatWindow] Search failed:', err);
    }
    setSearchLoading(false);
  }, [conversation.id]);

  // Debounce search input
  useEffect(() => {
    if (!showSearch || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, showSearch, handleSearch]);

  return (
    <div className={styles.chatWindow}>
      {/* Toolbar — available for all conversation types */}
      <div
        style={{
          padding: '6px 12px',
          borderBlockEnd: '1px solid var(--border-default)',
          display: 'flex',
          gap: 6,
          justifyContent: 'flex-end',
          alignItems: 'center',
          background: 'var(--bg-surface)',
        }}
      >
        {/* Search toggle */}
        <button
          onClick={() => { setShowSearch(!showSearch); setSearchQuery(''); setSearchResults([]); }}
          title={t('search.placeholder')}
          style={{
            padding: '6px 10px', fontSize: 12,
            background: showSearch ? 'var(--bg-tertiary)' : 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer', color: 'var(--text-primary)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>

        {/* Pinned messages */}
        <button
          onClick={handleShowPinned}
          title={t('conversation.pinned')}
          style={{
            padding: '6px 10px', fontSize: 12,
            background: showPinnedMessages ? 'var(--bg-tertiary)' : 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer', color: 'var(--text-primary)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
          </svg>
        </button>

        {/* Members — group chats only */}
        {conversation.conversationType === 'group' && (
          <button
            onClick={() => setShowMembers(!showMembers)}
            style={{
              padding: '6px 12px', fontSize: 12,
              background: showMembers ? 'var(--bg-tertiary)' : 'var(--bg-elevated)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer', color: 'var(--text-primary)',
            }}
          >
            👥 Members ({conversation.participants.filter(p => p.status === 'active' || p.status === 'invited').length})
          </button>
        )}
      </div>

      {/* Search bar */}
      {showSearch && (
        <div style={{
          padding: '8px 12px',
          borderBlockEnd: '1px solid var(--border-default)',
          background: 'var(--bg-surface)',
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search.placeholder')}
            autoFocus
            style={{
              flex: 1, padding: '6px 10px', fontSize: 13,
              background: 'var(--bg-primary)', border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
              outline: 'none',
            }}
          />
          {searchLoading && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>…</span>}
          {!searchLoading && searchResults.length > 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {t('search.resultsCount', { count: searchResults.length })}
            </span>
          )}
          {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {t('search.noResults')}
            </span>
          )}
        </div>
      )}

      {/* Search results overlay */}
      {showSearch && searchResults.length > 0 && (
        <div style={{
          maxHeight: 200, overflowY: 'auto',
          borderBlockEnd: '1px solid var(--border-default)',
          background: 'var(--bg-elevated)',
        }}>
          {searchResults.map((msg) => (
            <button
              key={msg.id}
              type="button"
              onClick={() => {
                // Scroll to the message in the main list
                const el = document.getElementById(`msg-${msg.id}`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.style.background = 'var(--bg-hover-strong)';
                  setTimeout(() => { el.style.background = ''; }, 2000);
                }
                setShowSearch(false);
                setSearchQuery('');
                setSearchResults([]);
              }}
              style={{
                display: 'flex', flexDirection: 'column', gap: 2,
                width: '100%', padding: '8px 14px',
                background: 'none', border: 'none', textAlign: 'start',
                cursor: 'pointer', borderBlockEnd: '1px solid var(--border-subtle)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  {msg.senderDisplayName || msg.senderScreenName || 'Unknown'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <span style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {msg.content || '(attachment)'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Pinned messages panel */}
      {showPinnedMessages && (
        <div style={{
          maxHeight: 250, overflowY: 'auto',
          borderBlockEnd: '1px solid var(--border-default)',
          background: 'var(--bg-elevated)',
        }}>
          <div style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', borderBlockEnd: '1px solid var(--border-subtle)' }}>
            📌 {t('conversation.pinned')}
          </div>
          {loadingPinned && <div style={{ padding: 14, fontSize: 13, color: 'var(--text-muted)' }}>…</div>}
          {!loadingPinned && pinnedMessages.length === 0 && (
            <div style={{ padding: 14, fontSize: 13, color: 'var(--text-muted)' }}>{t('conversation.noPinnedMessages')}</div>
          )}
          {pinnedMessages.map((msg) => (
            <button
              key={msg.id}
              type="button"
              onClick={() => {
                const el = document.getElementById(`msg-${msg.id}`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.style.background = 'var(--bg-hover-strong)';
                  setTimeout(() => { el.style.background = ''; }, 2000);
                }
                setShowPinnedMessages(false);
              }}
              style={{
                display: 'flex', flexDirection: 'column', gap: 2,
                width: '100%', padding: '8px 14px',
                background: 'none', border: 'none', textAlign: 'start',
                cursor: 'pointer', borderBlockEnd: '1px solid var(--border-subtle)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  {msg.senderDisplayName || msg.senderScreenName || 'Unknown'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <span style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {msg.content || '(attachment)'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Messages area */}
      <div
        className={styles.messagesContainer}
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          marginInlineEnd: showMembers ? 300 : 0,
          transition: 'margin-inline-end 0.2s',
        }}
      >
        {loading && (
          <div className={styles.loadingIndicator}>…</div>
        )}

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isOwn={msg.senderGuid.toUpperCase() === currentUserGuid.toUpperCase()}
            onEdit={handleEdit}
            onDelete={(isAdmin || msg.senderGuid.toUpperCase() === currentUserGuid.toUpperCase()) ? handleDelete : undefined}
            onPin={isAdmin || conversation.conversationType === 'direct' ? handlePin : undefined}
            onReact={handleReact}
            onReport={handleReport}
          />
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Send error */}
      {sendError && (
        <div className={styles.sendErrorBanner}>{sendError}</div>
      )}

      {/* Typing indicator */}
      {typingText && (
        <div className={styles.typingIndicator}>{typingText}</div>
      )}

      {/* Input */}
      <ChatInput
        conversationId={conversation.id}
        onSend={handleSend}
        editingContent={editingContent}
        onCancelEdit={() => setEditingMessageId(null)}
      />

      {/* Group Members Panel */}
      {showMembers && (
        <GroupMembersPanel
          conversationId={conversation.id}
          conversationType={conversation.conversationType}
          participants={conversation.participants}
          currentUserGuid={currentUserGuid}
          isAdmin={isAdmin}
          currentAvatarUrl={conversation.avatarUrl}
          conversationName={conversation.name}
          onConversationUpdated={onConversationUpdated}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  );
}
